import { useCallback, useEffect } from "react";
import type { Feature, GeoJSON, Geometry } from "geojson";
import { feature } from "@turf/helpers";
import { GeoJsonLayer } from "@deck.gl/layers";
import { scaleOrdinal } from "d3-scale";
import { schemeSet1 } from "d3-scale-chromatic";
import { color } from "d3-color";
import { SelectionType } from "src/js/stores/map/store";
import { rgbToDeckGlColour } from "react-migration/lib/map/createDeckGlColorInterpolator";
import { useMapLayer } from "react-migration/lib/map/useMapLayer";
import {
  GENERIC_GEOJSON_TITLE_SYMBOL,
  GENERIC_GEOJSON_COLOUR_SYMBOL,
} from "react-migration/lib/map/GenericGeoJsonLayer/constants";
import { useWorkbench } from "../../../Bundle/Workbench/WorkbenchContext";
import { useGeoJsonDeliverable } from "./useGeoJsonDeliverable";

/**
 * @todo Currently these AiDash specific properties are hard coded. If reusing
 * this component and functionality for user uploadable layers we could try and
 * infer sensible defaults from the data itself, or possibly present the user
 * with a wizard to configure the layer.
 */
const DEFAULT_GROUP = "DEFAULT";
const GROUP_PROPERTY_KEY = "habitatType";
const TITLE_PROPERTY_KEY = "habitatGroup";
const ID_PROPERTY_KEY = "serialId";
const COLOUR_SCALE = scaleOrdinal(schemeSet1.map((c) => color(c)));

export function useGeoJsonDeliverableMapLayer(signedUrl?: string, visible?: boolean) {
  const { geojson, ...fetchStatus } = useGeoJsonDeliverable(signedUrl);
  const { setDetailSelection, detailSelection } = useWorkbench();

  const featureIsFocussed = useCallback(
    (feature: Feature<Geometry, Record<string, unknown>>) => {
      if (!detailSelection) return true;
      if (feature.properties[ID_PROPERTY_KEY] === detailSelection.id) return true;
      return false;
    },
    [detailSelection]
  );

  useMapLayer(
    () => ({
      layer: new GeoJsonLayer({
        id: `generic-geojson-layer__${signedUrl}`,
        data: geojson as GeoJSON,
        getFillColor: (feature) => {
          const group = feature.properties[GROUP_PROPERTY_KEY] ?? DEFAULT_GROUP;
          const opacityModifier = featureIsFocussed(feature) ? 1 : 0.6;
          return rgbToDeckGlColour(COLOUR_SCALE(group)!.rgb(), 0.75 * 255 * opacityModifier);
        },
        getLineColor: (feature) => {
          const group = feature.properties[GROUP_PROPERTY_KEY] ?? DEFAULT_GROUP;
          const opacityModifier = featureIsFocussed(feature) ? 1 : 0.2;
          return rgbToDeckGlColour(COLOUR_SCALE(group)!.darker(0.5).rgb(), 255 * opacityModifier);
        },
        visible,
        transitions: {
          getFillColor: 150,
          getLineColor: 150,
        },
        autoHighlight: true,
        highlightColor: [0, 235, 151, 255 * 0.5],
        getLineWidth: 2,
        getPointRadius: 6,
        lineWidthUnits: "pixels",
        pointRadiusUnits: "pixels",
        stroked: true,
        filled: true,
        pickable: true,
        onClick: (info, event) => {
          /**
           * We don't want to treat this as a regular map click, instead we want
           * to catch it and set the detail selection as by virtue of this being
           * mounted we know the main selection is of type SITE
           */
          // @ts-expect-error incorrect typings
          event.srcEvent.domEvent?.preventDefault?.();

          const { geometry, properties } = info.object;
          const id = properties[ID_PROPERTY_KEY];
          const title = properties[TITLE_PROPERTY_KEY];
          const colour = COLOUR_SCALE(properties[GROUP_PROPERTY_KEY])?.rgb()?.toString();

          setDetailSelection({
            type: SelectionType.GENERIC_GEOJSON_FEATURE,
            id,
            feature: feature(geometry, {
              ...properties,

              // The following properties are used in the
              // GENERIC_GEOJSON_FEATURE's SelectionHeader.
              [GENERIC_GEOJSON_TITLE_SYMBOL]: title,
              [GENERIC_GEOJSON_COLOUR_SYMBOL]: colour,
            }),
          });
        },

        updateTriggers: {
          getFillColor: [featureIsFocussed],
          getLineColor: [featureIsFocussed],
        },
      }),
      zOrder: Infinity,
    }),
    [signedUrl, geojson, visible, featureIsFocussed, setDetailSelection],
    visible
  );

  useEffect(
    function clearDetailSelectionOnHide() {
      if (!visible && detailSelection?.type === SelectionType.GENERIC_GEOJSON_FEATURE) {
        setDetailSelection(undefined);
      }
    },
    [detailSelection?.type, visible, setDetailSelection]
  );

  return fetchStatus;
}
